import {
  NotificationService,
  OuiBoxComponent,
  OuiButtonComponent,
  OuiCheckboxComponent,
  OuiDividerComponent,
  OuiGridComponent,
  OuiRowComponent,
  OuiStackComponent,
  OuiTextComponent,
} from '@aiz-gl/omni-ui';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { InputComponent } from '../../../components/input/input.component';
import { PageComponent } from '../../../components/page/page';
import { RegionTagComponent } from '../../../components/region-tag/region-tag.component';
import { BusinessEntity } from '../../../models/business-entity.model';
import { User } from '../../../models/user.models';
import { AuthService } from '../../../services/auth/auth.service';
import { BusinessEntityService } from '../../../services/business-entity/business-entity.service';
import { CommonService } from '../../../services/common/common.service';
import {
  Permission,
  PermissionsService,
} from '../../../services/permissions/permissions.service';
import { UserService } from '../../../services/user/user.service';
import { adminOrPermissionsValidator } from './admin-or-permissions.validator';

@Component({
  selector: 'app-user-form',
  imports: [
    PageComponent,
    OuiStackComponent,
    OuiBoxComponent,
    OuiTextComponent,
    OuiDividerComponent,
    OuiCheckboxComponent,
    InputComponent,
    OuiRowComponent,
    OuiGridComponent,
    OuiButtonComponent,
    RegionTagComponent,
  ],
  templateUrl: './user-form.component.html',
})
export class UserFormPage {
  auth = inject(AuthService);
  entityService = inject(BusinessEntityService);
  permissionService = inject(PermissionsService);
  common = inject(CommonService);
  notifications = inject(NotificationService);
  userService = inject(UserService);
  router = inject(Router);

  // The router automatically feeds the signal 'userEmail'
  userEmail = input<string>();

  userData: Partial<User> | undefined = undefined;
  protected refreshed = true;

  userForm = new FormGroup(
    {
      firstName: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      email: new FormControl('', [Validators.required, Validators.email]),
      isAdmin: new FormControl(false),
      isLincUser: new FormControl(false),
      entities: new FormControl<string[]>([]),
      permissions: new FormControl<string[]>([]),
    },
    { validators: adminOrPermissionsValidator },
  );

  protected readonly editing = computed(() => this.userEmail() !== undefined);
  protected readonly formTitle = computed(() =>
    this.editing() ? 'Edit User' : 'Create a User',
  );
  protected readonly saving = signal<boolean>(false);
  protected readonly selectedUser = signal<User | undefined>(undefined);
  protected readonly selectedEntities = signal<string[] | undefined>(undefined);
  protected readonly selectedPermissions = signal<string[] | undefined>(
    undefined,
  );

  constructor() {
    // Access state immediately in the constructor (or ngOnInit)
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras.state) {
      this.userData = navigation.extras.state;
    }

    effect(() => {
      const email = this.userEmail();

      if (email) {
        this.userForm.controls['email'].setValue(email);
        this.userForm.controls['email'].disable();
      } else {
        this.userForm.controls['email'].enable();
      }

      if (this.userData) {
        this.populateForm(this.userData);
      }

      // Look up user data and populate the form if the user data is not already present.
      // This usually happens if the page is refreshed or coming from a direct link.
      if (email && !this.userData) {
        this.userService.getUser$(email).subscribe((user) => {
          this.userData = user;
          this.populateForm(user);
        });

        this.forceRerender();
      }
    });
  }

  populateForm(user: Partial<User>) {
    this.userForm.patchValue(user);
    this.userForm.updateValueAndValidity();
  }

  protected saveUser() {
    this.saving.set(true);
    const user = this.userData;
    const updates = this.userForm.getRawValue();
    const updatedUser = {
      ...user,
      ...updates,
    } as User;

    if (this.editing()) {
      this.updateUser(updatedUser);
    } else {
      // TODO: Check if this logic is still relevant as the active flag is no longer returned by the API.
      // Check if email is in inactiveUsers$
      this.userService.inactiveUsers$.subscribe((inactiveUsers) => {
        const inactiveUserEmail = inactiveUsers.find(
          (u) => u.email === updatedUser.email,
        );
        if (inactiveUserEmail) {
          this.confirmReactivate(inactiveUserEmail);
          this.saving.set(false);
        } else {
          this.userService.createUser(updatedUser as User).subscribe({
            next: () => {
              this.userService.users.reload();
              this.notifications.showSnackbar({
                message: 'User Created Successfully',
                variant: 'success',
                symbolName: 'shield_person',
              });
            },
            error: (response: HttpErrorResponse) => {
              this.saving.set(false);
              this.handleCreateUserErrorResponse(response);
            },
            complete: () => {
              this.saving.set(false);
              this.navigateToUserPage();
            },
          });
        }
      });
    }
  }

  handleCreateUserErrorResponse(response: HttpErrorResponse) {
    const { error } = response;
    this.notifications.showAlert({
      message: error.message,
      variant: 'warn',
      symbolName: 'account_circle_off',
    });
  }

  confirmReactivate = (user: User) =>
    this.notifications.showConfirmation({
      title: 'Reactivate User',
      message: `A user with the email "${user.email}" already exists but is currently inactive. Would you like to reactivate this user?`,
      action: () => this.updateUser(user),
      actionLabel: 'Reactivate',
      dismissLabel: 'Cancel',
      color: 'secondary',
    });

  updateUser(updatedUser: User) {
    this.userService.saveUser(updatedUser).subscribe({
      next: () => {
        this.userService.users.reload();
        this.notifications.showSnackbar({
          message: 'User Updated Successfully',
          variant: 'success',
          symbolName: 'shield_person',
        });
        this.navigateToUserPage();
      },
      error: (response: HttpErrorResponse) => {
        const { error } = response;
        const errorMessage =
          error && Array.isArray(response.error)
            ? error
                .map((err: { error_message: never }) => err.error_message)
                .join(', ')
            : 'An unexpected error occurred.';
        this.notifications.showAlert({
          message: errorMessage,
          variant: 'warn',
          symbolName: 'account_circle_off',
        });
        // this.showDialog.set(false);
      },
      complete: () => {
        // this.showDialog.set(false);
      },
    });
  }

  protected cancelSave() {
    this.navigateToUserPage();
  }

  protected navigateToUserPage() {
    this.router.navigate([`/user-management`]);
  }

  protected toggleAdmin() {
    this.populateForm({ isAdmin: !this.userForm.value.isAdmin });
  }

  protected toggleLincAdmin() {
    this.populateForm({ isLincUser: !this.userForm.value.isLincUser });
  }

  protected togglePermission(permission: Permission) {
    const permissions = this.userForm.value.permissions || [];
    this.populateForm({
      permissions: this.common.toggleStringInArray(permissions, permission.id),
    });
  }

  protected toggleEntity(entity: BusinessEntity) {
    const entities = this.userForm.value.entities || [];
    this.populateForm({
      entities: this.common.toggleStringInArray(entities, entity.code),
    });
  }

  protected isAssigned(permission: Permission) {
    return this.userData?.permissions?.includes(permission.id) || false;
  }

  protected isAssociated(entity: BusinessEntity) {
    return this.userData?.entities?.includes(entity.code) || false;
  }

  forceRerender(): void {
    this.refreshed = false;
    setTimeout(() => (this.refreshed = true), 0);
  }
}
